import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Card, Notice, TextField, useSnackbar } from '@sauda/ui/kit';
import { useAuth } from '../auth/AuthContext.js';
import { api } from '../lib/api.js';

const MOBILE = /^(?:\+?91)?[6-9]\d{9}$/;
const clean = (value: string): string => value.replace(/[\s-]/g, '');
/** `+919876500011` as the ten digits a person types. */
const national = (value: string | null): string => (value ?? '').replace(/^\+?91/, '');

/** The agency's own call and WhatsApp numbers: what clients and owners reach when a branch has none. */
export function ContactCard({
  phone,
  whatsapp,
  readOnly,
}: {
  phone: string | null;
  whatsapp: string | null;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const auth = useAuth();
  const cache = useQueryClient();
  const snack = useSnackbar();
  const [callNumber, setCallNumber] = useState<string | null>(null);
  const [chatNumber, setChatNumber] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const shownCall = callNumber ?? national(phone);
  const shownChat = chatNumber ?? national(whatsapp);
  const valid = MOBILE.test(clean(shownCall)) && MOBILE.test(clean(shownChat));
  const changed = callNumber !== null || chatNumber !== null;

  const save = useMutation({
    mutationFn: () =>
      api('/agency/contact', {
        method: 'PUT',
        body: { phone: clean(shownCall), whatsapp: clean(shownChat) },
      }),
    onSuccess: async () => {
      setError(null);
      setCallNumber(null);
      setChatNumber(null);
      await cache.invalidateQueries({ queryKey: ['brand', auth.agencyId] });
      snack({ text: t('brand.contactSaved') });
    },
    onError: () => setError(t('brand.contactError')),
  });

  return (
    <Card>
      <h2 className="k-t-title-sm">{t('brand.contact')}</h2>
      <p className="k-t-body k-muted">{t('brand.contactHelp')}</p>
      {phone === null ? <Notice tone="info">{t('brand.contactMissing')}</Notice> : null}
      {error === null ? null : <Notice tone="error">{error}</Notice>}
      <TextField
        label={t('brand.contactPhone')}
        type="tel"
        inputMode="numeric"
        prefix="+91"
        value={shownCall}
        onChange={(e) => setCallNumber(e.target.value)}
      />
      <TextField
        label={t('brand.contactWhatsapp')}
        type="tel"
        inputMode="numeric"
        prefix="+91"
        value={shownChat}
        onChange={(e) => setChatNumber(e.target.value)}
      />
      <div>
        <Button
          variant="tonal"
          loading={save.isPending}
          disabled={!changed || !valid || readOnly}
          onClick={() => save.mutate()}
        >
          {t('brand.contactSave')}
        </Button>
      </div>
    </Card>
  );
}
